import { Button } from "@mui/material";
import ExcelJS from "exceljs";
import logoImg from "../../images/logobpoexcel.png";
import styles from "./styles.module.css";
import CircleLoader from "react-spinners/CircleLoader";
import React, { useState } from "react";

const ExportExcelConciliacao = ({
  conciliacao,
  categorias,
  referencia,
  cliente,
  capa,
  grupoEconomico,
  cnpjSelecionado,
}) => {
  const [loadingExport, setLoadingExport] = useState(false);

  const sanitizeSheetName = (name) => {
    const safe = (name || "Categoria")
      .replace(/[:\\/?*\[\]]/g, "") // remove caracteres inválidos
      .trim()
      .substring(0, 31); // 31 chars no Excel
    return safe;
  };

  // conversão para string segura (para valores de célula ExcelJS)
  const asString = (val) => {
    if (val == null) return "";
    if (typeof val === "string") return val;
    if (typeof val === "number") return String(val);
    if (typeof val === "object" && "text" in val) return String(val.text ?? "");
    return String(val);
  };

  const handleExport = async () => {
    const abasJaCriadas = new Set();
    setLoadingExport(true);

    const workbook = new ExcelJS.Workbook();

    // mapa: código -> nome final da aba criada
    const sheetNameByCode = new Map();

    const imageId = workbook.addImage({
      base64: await fetchImageAsBase64(logoImg),
      extension: "png",
    });

    const parseNumber = (value) => {
      if (typeof value === "string") {
        const clean = value.trim().replace(",", ".");
        const parsed = Number(clean);
        return isNaN(parsed) ? 0 : parsed;
      }
      return typeof value === "number" ? value : 0;
    };

    // === Capa ===
    const sheet = workbook.addWorksheet("Capa", {
      properties: { tabColor: { argb: "1072BA" } },
    });
    const capaSheet = sheet; // manter referência fixa à capa

    workbook.views = [{ activeTab: 0 }];
    sheet.getCell("B4").value = "Grupo Econômico:";
    sheet.getCell("C4").value = grupoEconomico || "-";

    sheet.getCell("E4").value = "CNPJ:";
    sheet.getCell("F4").value = cnpjSelecionado || "-";

    ["B4", "E4"].forEach((cellRef) => {
      const cell = sheet.getCell(cellRef);
      cell.font = { bold: true };
    });

    // Logo
    sheet.addImage(imageId, {
      tl: { col: 4, row: 0 },
      ext: { width: 200, height: 50 },
    });

    sheet.addRow([]);
    sheet.addRow([]);
    sheet.addRow([]);

    const headerRow = sheet.getRow(5);
    [
      "Tipo",
      "Grupo",
      "Código",
      "Conta",
      "Descrição",
      "Saldo anterior",
      "Débito",
      "Crédito",
      "Saldo atual",
      "Suporte",
      "Diferença",
      "Observações",
      "Variação",
    ].forEach((header, index) => {
      const cell = headerRow.getCell(index + 2); // começa na coluna B (index 2)
      cell.value = header;
      cell.font = { bold: true };
      cell.alignment = { horizontal: "center" };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "00BFFF" },
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    capa.forEach((linha, linhaIndex) => {
      const row = sheet.getRow(linhaIndex + 6); // dados a partir da linha 6
      const isSintetico = (linha.tipo || "").toUpperCase() === "SINTETICO";

      const valores = [
        linha.tipo,
        linha.grupo,
        linha.codigo,
        linha.classificacao,
        linha.descricao,
        parseNumber(linha.saldo_anterior),
        parseNumber(linha.debito),
        parseNumber(linha.credito),
        parseNumber(linha.saldo_atual),
        linha.suporte,
        null, // diferença sempre vazia
        linha.observacoes,
        linha.variacao,
      ];

      valores.forEach((valor, colIndex) => {
        const cell = row.getCell(colIndex + 2); // começa na coluna B
        cell.value = valor;
        cell.font = { size: 10 };
        cell.alignment = { horizontal: "left" };
        cell.border = {
          bottom: { style: "thin" },
        };

        if (isSintetico) {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "D9D9D9" },
          };
        }

        // Colunas numéricas (Saldo anterior, Débito, Crédito, Saldo atual)
        if ([6, 7, 8, 9].includes(colIndex + 1) && typeof valor === "number") {
          cell.numFmt = "R$ #,##0.00";
        }
      });
    });

    // Auto ajuste das colunas da Capa
    sheet.columns.slice(1).forEach((column) => {
      let maxLength = 10;
      column.eachCell?.({ includeEmpty: true }, (cell) => {
        const length = cell.value ? cell.value.toString().length : 0;
        if (length > maxLength) maxLength = length;
      });
      column.width = maxLength + 2;
    });

    const isAtivoOuPassivo = (classificacao) => {
      const digits = String(classificacao ?? "").replace(/\D/g, "");
      return digits.startsWith("1") || digits.startsWith("2");
    };

    const categoriasSinteticas = categorias.filter(
      (cat) =>
        cat.nivel === "SINTETICO" &&
        cat.classificacao?.length === 5 &&
        isAtivoOuPassivo(cat.classificacao)
    );

    for (const categoria of categoriasSinteticas) {
      const baseClass = categoria.classificacao;
      const abaDescricao = categoria.codigo;

      // Flag para contas que não devem exibir razões
      const ignorarRazoes = ["11102", "11103"].includes(baseClass);

      // Apenas as analíticas que também aparecem na CAPA como ANALITICO e que pertencem a este sintetico
      const baseDigits = String(baseClass ?? "").replace(/\D/g, "");

      const normalize = (s) =>
        String(s ?? "")
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/\s+/g, " ")
          .trim();

      const capaAnaliticos = (capa || []).filter(
        (l) =>
          String(l?.tipo || "").toUpperCase() === "ANALITICO" &&
          String(l?.classificacao ?? "").replace(/\D/g, "").startsWith(baseDigits)
      );

      const allowedByClassif = new Set(
        capaAnaliticos
          .map((l) => String(l?.classificacao ?? "").replace(/\D/g, ""))
          .filter((v) => v.length > 0)
      );
      const allowedByCodigo = new Set(
        capaAnaliticos
          .map((l) => String(l?.codigo ?? "").trim())
          .filter(Boolean)
      );
      const allowedByDescricao = new Set(
        capaAnaliticos.map((l) => normalize(l?.descricao))
      );

      let categoriasAnaliticasFilhas = categorias.filter(
        (cat) =>
          cat.nivel === "ANALITICO" && cat.classificacao?.startsWith(baseClass)
      );
      if (capaAnaliticos.length > 0) {
        categoriasAnaliticasFilhas = categoriasAnaliticasFilhas.filter((c) => {
          const cDigits = String(c?.classificacao ?? "").replace(/\D/g, "");
          const cCodigo = String(c?.codigo ?? "").trim();
          const cDesc = normalize(c?.descricao);
          return (
            (cDigits && allowedByClassif.has(cDigits)) ||
            (cCodigo && allowedByCodigo.has(cCodigo)) ||
            (cDesc && allowedByDescricao.has(cDesc))
          );
        });
      }

      // Linhas analíticas da CAPA que pertencem a este sintético
      const capaAnaliticosBase = (capa || []).filter(
        (l) =>
          String(l?.tipo || "").toUpperCase() === "ANALITICO" &&
          String(l?.classificacao ?? "").replace(/\D/g, "").startsWith(
            String(baseClass ?? "").replace(/\D/g, "")
          )
      );

      if (capaAnaliticosBase.length === 0) continue;

      // Assinatura única pra evitar duplicar abas idênticas (com base na CAPA)
      const assinaturaAba = capaAnaliticosBase
        .map(
          (l) =>
            `${l.codigo}-${l.classificacao}-${l.saldo_atual}-${l.descricao}`
        )
        .sort()
        .join("|");

      if (abasJaCriadas.has(assinaturaAba)) {
        continue;
      }
      abasJaCriadas.add(assinaturaAba);

      let baseName = sanitizeSheetName(abaDescricao);
      let finalName = baseName;
      let counter = 1;
      while (workbook.getWorksheet(finalName)) {
        counter++;
        const suffix = ` (${counter})`;
        finalName = baseName.substring(0, 31 - suffix.length) + suffix;
      }

      const sheet = workbook.addWorksheet(finalName);

      // mapeia código -> nome da aba criada
      sheetNameByCode.set(String(categoria.codigo), finalName);

      sheet.addImage(imageId, {
        tl: { col: 4, row: 0 },
        ext: { width: 200, height: 50 },
      });

      // Helper: detecta textos que parecem valores monetários (ex.: 3.285.590,63C)
      const isValorMonetarioLike = (txt) => {
        if (typeof txt !== "string") return false;
        const t = txt.trim();
        if (!t) return false;
        // números com . como separador de milhar, , como decimal e opcional C/D no final
        return /^[-+\s]?[0-9.]+(?:,[0-9]{2})?\s*[CDcd]?$/.test(t);
      };

      // Busca descrição de conta a partir do código nas categorias (analíticas)
      const getDescricaoCategoriaByCodigo = (codigo) => {
        if (!codigo) return "";
        const cat = categorias.find(
          (c) => String(c.codigo).trim() === String(codigo).trim()
        );
        return cat?.descricao || "";
      };

      // Helper: escolhe um histórico seguro (nome) evitando gravar valores no lugar do nome
      const safeHistorico = (primary, ...alternates) => {
        const candidates = [primary, ...alternates].filter(
          (v) => typeof v === "string" && v.trim().length > 0
        );
        for (const c of candidates) {
          if (!isValorMonetarioLike(c)) return c; // retorna o primeiro que não seja "valor"
        }
        return candidates[0] || "Sem detalhamento";
      };

      // Deriva a "Conta" priorizando a(s) contas das razões; se não houver, cai para item.codigo
      const deriveConta = (item) => {
        try {
          const contas = Array.isArray(item?.razoes)
            ? Array.from(
                new Set(
                  item.razoes
                    .map((r) => (r?.conta != null ? String(r.conta).trim() : ""))
                    .filter((c) => c.length > 0)
                )
              )
            : [];
          // Preferir um formato mais completo (com pontos/hífens) ou pelo menos 4 dígitos
          const preferida =
            contas.find((c) => /[.\-]/.test(c)) ||
            contas.find((c) => c.replace(/\D/g, "").length >= 4);
          if (preferida) return preferida;
          if (contas.length === 1) return contas[0];
        } catch {}
        return item?.codigo;
      };

      // Constrói as linhas a partir da CAPA (apenas as analíticas permitidas)
      const linhasBalancete = capaAnaliticosBase.map((l) => {
        const hist = safeHistorico(
          l?.descricao,
          getDescricaoCategoriaByCodigo(l?.codigo),
          categoria?.descricao
        );
        return {
          data: formatReferenciaUltimoDia(referencia),
          documento: "",
          historico: hist,
          conta: String(l?.classificacao ?? l?.codigo ?? ""),
          saldo: parseNumber(l?.saldo_atual),
        };
      });

      const linhaInicio = 4;

      sheet.getCell(`B${linhaInicio}`).value = "Cliente:";
      sheet.getCell(`C${linhaInicio}`).value = grupoEconomico || "";
      sheet.getCell(`B${linhaInicio + 1}`).value = "Descrição:";
      sheet.getCell(`C${linhaInicio + 1}`).value = categoria.descricao;
      sheet.getCell(`K${linhaInicio}`).value = "Suporte:";
      sheet.getCell(`K${linhaInicio + 1}`).value = "Diferença:";

      ["K", "L"].forEach((col) => {
        for (let i = linhaInicio; i <= linhaInicio + 1; i++) {
          const cell = sheet.getCell(`${col}${i}`);
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "D9D9D9" },
          };
          cell.font = { bold: true };
          cell.alignment = { horizontal: "left" };
          cell.border = {
            top: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
            left: { style: "thin" },
          };
        }
      });
      ["B"].forEach((col) => {
        for (let i = linhaInicio; i <= linhaInicio + 1; i++) {
          const cell = sheet.getCell(`${col}${i}`);
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "ffffff" },
          };
          cell.font = { bold: true };
          cell.alignment = { horizontal: "left" };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
          };
        }
      });
      ["C", "D", "E", "F", "G", "H", "I", "J"].forEach((col) => {
        for (let i = linhaInicio; i <= linhaInicio + 1; i++) {
          const cell = sheet.getCell(`${col}${i}`);
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "ffffff" },
          };
          cell.font = { bold: true };
          cell.alignment = { horizontal: "left" };
          cell.border = { top: { style: "thin" }, bottom: { style: "thin" } };
        }
      });
      sheet.addRow([]);

      const headerRow = sheet.addRow([]);
      [
        "Data",
        "Documento",
        "Histórico",
        "",
        "",
        "",
        "",
        "",
        "",
        "Conta",
        "Saldo",
      ].forEach((header, idx) => {
        const cell = headerRow.getCell(idx + 2);
        cell.value = header;
        cell.font = { bold: true };
        cell.alignment = { horizontal: "center" };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "D9D9D9" },
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      const totalSaldo = linhasBalancete.reduce((acc, l) => acc + l.saldo, 0);

      linhasBalancete.forEach((linha) => {
        const row = sheet.addRow([]);
        row.getCell(2).value = linha.data;
        row.getCell(3).value = linha.documento;
        row.getCell(4).value = linha.historico;
        row.getCell(5).value = "";
        row.getCell(6).value = "";
        row.getCell(7).value = "";
        row.getCell(8).value = "";
        row.getCell(9).value = "";
        row.getCell(10).value = "";
        row.getCell(11).value = linha.conta;
        row.getCell(12).value = linha.saldo;
        row.getCell(12).numFmt = "R$ #,##0.00";

        row.eachCell((cell) => {
          cell.font = { size: 10 };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      });

      const totalRow = sheet.addRow([]);
      totalRow.getCell(2).value = "TOTAL";
      sheet.mergeCells(`C${totalRow.number}:K${totalRow.number}`);
      totalRow.getCell(12).value = totalSaldo;
      totalRow.getCell(12).numFmt = "R$ #,##0.00";
      totalRow.getCell(12).font = { bold: true };
      totalRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "D9D9D9" },
        };
      });

      const observacaoLinhas = 3;
      const observacaoComeco = sheet.lastRow.number + 1;
      for (let i = 0; i < observacaoLinhas; i++) {
        sheet.addRow([]);
      }
      const observacaoFinal = observacaoComeco + observacaoLinhas - 1;

      ["B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L"].forEach((col) => {
        sheet.getCell(`${col}${observacaoComeco}`).border = {
          top: { style: "thin" },
        };
        sheet.getCell(`${col}${observacaoFinal}`).border = {
          bottom: { style: "thin" },
        };
      });
      ["B", "L"].forEach((col) => {
        for (let i = observacaoComeco; i <= observacaoFinal; i++) {
          sheet.getCell(`${col}${i}`).border = {
            ...sheet.getCell(`${col}${i}`).border,
            left: { style: "thin" },
            right: { style: "thin" },
            bottom: { style: "thin" },
            top: { style: "thin" },
          };
        }
      });

      const total2 = sheet.addRow([]);
      total2.getCell(2).value = "TOTAL";
      sheet.mergeCells(`C${total2.number}:K${total2.number}`);
      total2.getCell(12).numFmt = "R$ #,##0.00";
      total2.getCell(12).font = { bold: true };
      for (let i = 2; i <= 12; i++) {
        const cell = total2.getCell(i);
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "D9D9D9" },
        };
      }

      const suporteLinhas = 12;
      const suporteStart = sheet.lastRow.number + 1;

      for (let i = 0; i < suporteLinhas; i++) {
        sheet.addRow([]);
      }
      const suporteEnd = suporteStart + suporteLinhas - 1;

      const suporteHeaderRow = sheet.addRow([]);
      ["Suporte 1", "Suporte 2", "Suporte 3", "Suporte 4", "Suporte 5"].forEach(
        (label, i) => {
          suporteHeaderRow.getCell(2 + i).value = label; // B..F
          suporteHeaderRow.getCell(2 + i).font = { bold: true };
          suporteHeaderRow.getCell(2 + i).alignment = { horizontal: "center" };
        }
      );

      const linhaSuporte = sheet.addRow([]);

      ["B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L"].forEach((col) => {
        sheet.getCell(`${col}${suporteStart}`).border = {
          top: { style: "thin" },
        };
        sheet.getCell(`${col}${suporteEnd}`).border = {
          bottom: { style: "thin" },
        };
      });
      ["B", "L"].forEach((col) => {
        for (let i = suporteStart; i <= suporteEnd; i++) {
          sheet.getCell(`${col}${i}`).border = {
            ...sheet.getCell(`${col}${i}`).border,
          };
        }
      });

      const total3 = sheet.addRow([]);
      total3.getCell(2).value = "TOTAL";
      sheet.mergeCells(`C${total3.number}:K${total3.number}`);
      total3.getCell(12).value = "";
      total3.getCell(12).numFmt = "R$ #,##0.00";
      total3.getCell(12).font = { bold: true };
      total3.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "D9D9D9" },
        };
      });

      const linhaCriado = sheet.addRow([]);
      linhaCriado.getCell("B").value = "Criado por:";
      linhaCriado.getCell("D").value = "Data:";
      const linhaRevisado = sheet.addRow([]);
      linhaRevisado.getCell("B").value = "Revisado por:";
      linhaRevisado.getCell("D").value = "Data:";

      const linhaCriadoNum = linhaCriado.number;
      const linhaRevisadoNum = linhaRevisado.number;

      ["B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L"].forEach((col) => {
        [linhaCriadoNum, linhaRevisadoNum].forEach((rowNum) => {
          const cell = sheet.getCell(`${col}${rowNum}`);
          if (col === "B" || col === "L") {
            cell.border = {
              ...cell.border,
              left: col === "B" ? { style: "thin" } : undefined,
              right: col === "L" ? { style: "thin" } : undefined,
            };
          }
          if (rowNum === linhaCriadoNum) {
            cell.border = { ...cell.border, top: { style: "thin" } };
          }
          if (rowNum === linhaRevisadoNum) {
            cell.border = { ...cell.border, bottom: { style: "thin" } };
          }
        });
      });

      // Borda perimetral do conteúdo da folha
      const firstCol = 2;
      const firstRow = 4;
      const lastCol = 12;
      const lastRow = sheet.lastRow.number;
      for (let row = firstRow; row <= lastRow; row++) {
        for (let col = firstCol; col <= lastCol; col++) {
          const cell = sheet.getCell(row, col);
          const border = {};
          if (row === firstRow) border.top = { style: "medium" };
          if (row === lastRow) border.bottom = { style: "medium" };
          if (col === firstCol) border.left = { style: "medium" };
          if (col === lastCol) border.right = { style: "medium" };
          cell.border = { ...cell.border, ...border };
        }
      }

      sheet.columns.forEach((column) => {
        let maxLength = 10;
        column.eachCell?.({ includeEmpty: true }, (cell) => {
          const columnLength = cell.value ? cell.value.toString().length : 0;
          if (columnLength > maxLength) maxLength = columnLength;
        });
        column.width = maxLength + 2;
      });

      // Garante que a coluna "Histórico" (coluna D = índice 4) fique como texto
      try {
        const historicoCol = sheet.getColumn(4);
        historicoCol.numFmt = "@";
      } catch {}
    } // fim loop categorias

    // === Links na Capa (coluna Grupo) para abas cujo nome = Código ===
    for (let r = 6; r < 6 + capa.length; r++) {
      const row = capaSheet.getRow(r);

      // Código está na coluna D (4)
      const codigoVal = row.getCell(4).value;
      const codigo = asString(codigoVal).trim();

      const targetSheet = sheetNameByCode.get(codigo);
      if (!targetSheet) continue;

      // Grupo = coluna C (3)
      const grupoCell = row.getCell(3);
      grupoCell.value = {
        text: "Link da Aba",
        hyperlink: `#'${targetSheet}'!A1`,
      };
      grupoCell.value = {
        text: "Link da Aba",
        hyperlink: `#'${targetSheet}'!A1`,
      };
      grupoCell.font = { underline: true, color: { argb: "FF0563C1" } };
      grupoCell.alignment = { horizontal: "left" };
    }

    // Ajuste da largura da coluna Grupo
    const colGrupo = capaSheet.getColumn(3);
    colGrupo.width = Math.max(colGrupo.width ?? 12, 18);

    // === Export ===
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `conciliacao_${referencia}.xlsx`;
    link.click();

    setLoadingExport(false);
  };

  const formatReferenciaUltimoDia = (referencia) => {
    let ano, mes;

    if (typeof referencia === "string") {
      if (referencia.includes("-")) {
        [ano, mes] = referencia.split("-").map(Number);
      } else if (referencia.includes("/")) {
        [mes, ano] = referencia.split("/").map(Number);
      }
    } else if (referencia instanceof Date) {
      ano = referencia.getFullYear();
      mes = referencia.getMonth() + 1;
    }

    if (!ano || !mes) return "Data inválida";

    const ultimoDia = new Date(ano, mes, 0);
    const dia = String(ultimoDia.getDate()).padStart(2, "0");
    const mesStr = String(ultimoDia.getMonth() + 1).padStart(2, "0");
    return `${dia}/${mesStr}/${ano}`;
  };

  const fetchImageAsBase64 = async (url) => {
    const response = await fetch(url);
    const blob = await response.blob();
    return await convertBlobToBase64(blob);
  };

  const convertBlobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {loadingExport ? (
        <CircleLoader
          color="#18316e"
          loading={loadingExport}
          size={80}
          aria-label="Loading Spinner"
          data-testid="loader"
        />
      ) : (
        <Button
          className={styles.botaoNovo}
          onClick={handleExport}
          size="small"
          style={{ width: "fit-content" }}
        >
          Exportar Excel
        </Button>
      )}
    </div>
  );
};

export default ExportExcelConciliacao;
